/**
 * Country Codes Utility
 * 
 * Centralized country code data and utility functions.
 * This is the single source of truth for all country codes in the application.
 * 
 * Format: { code: string, country: string (ISO 3166-1 alpha-2), flag: string (emoji), name: string }
 */

export interface Country {
    code: string; // Phone code with + prefix (e.g., "+971")
    country: string; // ISO 3166-1 alpha-2 country code (e.g., "AE")
    flag: string; // Flag emoji (e.g., "🇦🇪")
    name: string; // Country name (e.g., "United Arab Emirates")
    nationality: string; // Nationality/demonym (e.g., "Emirati")
}

/**
 * Complete list of countries with phone codes
 * Sorted by region for maintainability
 */
export const COUNTRIES: readonly Country[] = [
    // Middle East / GCC
    { code: '+971', country: 'AE', flag: '🇦🇪', name: 'United Arab Emirates', nationality: 'Emirati' },
    { code: '+966', country: 'SA', flag: '🇸🇦', name: 'Saudi Arabia', nationality: 'Saudi Arabian' },
    { code: '+973', country: 'BH', flag: '🇧🇭', name: 'Bahrain', nationality: 'Bahraini' },
    { code: '+965', country: 'KW', flag: '🇰🇼', name: 'Kuwait', nationality: 'Kuwaiti' },
    { code: '+974', country: 'QA', flag: '🇶🇦', name: 'Qatar', nationality: 'Qatari' },
    { code: '+968', country: 'OM', flag: '🇴🇲', name: 'Oman', nationality: 'Omani' },
    { code: '+967', country: 'YE', flag: '🇾🇪', name: 'Yemen', nationality: 'Yemeni' },
    { code: '+962', country: 'JO', flag: '🇯🇴', name: 'Jordan', nationality: 'Jordanian' },
    { code: '+961', country: 'LB', flag: '🇱🇧', name: 'Lebanon', nationality: 'Lebanese' },
    { code: '+970', country: 'PS', flag: '🇵🇸', name: 'Palestine', nationality: 'Palestinian' },
    { code: '+963', country: 'SY', flag: '🇸🇾', name: 'Syria', nationality: 'Syrian' },
    { code: '+964', country: 'IQ', flag: '🇮🇶', name: 'Iraq', nationality: 'Iraqi' },
    { code: '+972', country: 'IL', flag: '🇮🇱', name: 'Israel', nationality: 'Israeli' },

    // Asia
    { code: '+93', country: 'AF', flag: '🇦🇫', name: 'Afghanistan', nationality: 'Afghan' },
    { code: '+880', country: 'BD', flag: '🇧🇩', name: 'Bangladesh', nationality: 'Bangladeshi' },
    { code: '+975', country: 'BT', flag: '🇧🇹', name: 'Bhutan', nationality: 'Bhutanese' },
    { code: '+673', country: 'BN', flag: '🇧🇳', name: 'Brunei', nationality: 'Bruneian' },
    { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China', nationality: 'Chinese' },
    { code: '+852', country: 'HK', flag: '🇭🇰', name: 'Hong Kong', nationality: 'Hong Kong' },
    { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India', nationality: 'Indian' },
    { code: '+62', country: 'ID', flag: '🇮🇩', name: 'Indonesia', nationality: 'Indonesian' },
    { code: '+98', country: 'IR', flag: '🇮🇷', name: 'Iran', nationality: 'Iranian' },
    { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan', nationality: 'Japanese' },
    { code: '+7', country: 'KZ', flag: '🇰🇿', name: 'Kazakhstan', nationality: 'Kazakh' },
    { code: '+996', country: 'KG', flag: '🇰🇬', name: 'Kyrgyzstan', nationality: 'Kyrgyz' },
    { code: '+855', country: 'KH', flag: '🇰🇭', name: 'Cambodia', nationality: 'Cambodian' },
    { code: '+82', country: 'KR', flag: '🇰🇷', name: 'South Korea', nationality: 'South Korean' },
    { code: '+850', country: 'KP', flag: '🇰🇵', name: 'North Korea', nationality: 'North Korean' },
    { code: '+856', country: 'LA', flag: '🇱🇦', name: 'Laos', nationality: 'Lao' },
    { code: '+94', country: 'LK', flag: '🇱🇰', name: 'Sri Lanka', nationality: 'Sri Lankan' },
    { code: '+95', country: 'MM', flag: '🇲🇲', name: 'Myanmar', nationality: 'Burmese' },
    { code: '+976', country: 'MN', flag: '🇲🇳', name: 'Mongolia', nationality: 'Mongolian' },
    { code: '+60', country: 'MY', flag: '🇲🇾', name: 'Malaysia', nationality: 'Malaysian' },
    { code: '+977', country: 'NP', flag: '🇳🇵', name: 'Nepal', nationality: 'Nepali' },
    { code: '+92', country: 'PK', flag: '🇵🇰', name: 'Pakistan', nationality: 'Pakistani' },
    { code: '+63', country: 'PH', flag: '🇵🇭', name: 'Philippines', nationality: 'Filipino' },
    { code: '+65', country: 'SG', flag: '🇸🇬', name: 'Singapore', nationality: 'Singaporean' },
    { code: '+66', country: 'TH', flag: '🇹🇭', name: 'Thailand', nationality: 'Thai' },
    { code: '+992', country: 'TJ', flag: '🇹🇯', name: 'Tajikistan', nationality: 'Tajik' },
    { code: '+993', country: 'TM', flag: '🇹🇲', name: 'Turkmenistan', nationality: 'Turkmen' },
    { code: '+90', country: 'TR', flag: '🇹🇷', name: 'Turkey', nationality: 'Turkish' },
    { code: '+998', country: 'UZ', flag: '🇺🇿', name: 'Uzbekistan', nationality: 'Uzbek' },
    { code: '+84', country: 'VN', flag: '🇻🇳', name: 'Vietnam', nationality: 'Vietnamese' },
    { code: '+853', country: 'MO', flag: '🇲🇴', name: 'Macao', nationality: 'Macanese' },
    { code: '+886', country: 'TW', flag: '🇹🇼', name: 'Taiwan', nationality: 'Taiwanese' },
    { code: '+960', country: 'MV', flag: '🇲🇻', name: 'Maldives', nationality: 'Maldivian' },
    { code: '+670', country: 'TL', flag: '🇹🇱', name: 'East Timor', nationality: 'Timorese' },

    // Europe
    { code: '+355', country: 'AL', flag: '🇦🇱', name: 'Albania', nationality: 'Albanian' },
    { code: '+376', country: 'AD', flag: '🇦🇩', name: 'Andorra', nationality: 'Andorran' },
    { code: '+374', country: 'AM', flag: '🇦🇲', name: 'Armenia', nationality: 'Armenian' },
    { code: '+43', country: 'AT', flag: '🇦🇹', name: 'Austria', nationality: 'Austrian' },
    { code: '+994', country: 'AZ', flag: '🇦🇿', name: 'Azerbaijan', nationality: 'Azerbaijani' },
    { code: '+375', country: 'BY', flag: '🇧🇾', name: 'Belarus', nationality: 'Belarusian' },
    { code: '+32', country: 'BE', flag: '🇧🇪', name: 'Belgium', nationality: 'Belgian' },
    { code: '+387', country: 'BA', flag: '🇧🇦', name: 'Bosnia and Herzegovina', nationality: 'Bosnian' },
    { code: '+359', country: 'BG', flag: '🇧🇬', name: 'Bulgaria', nationality: 'Bulgarian' },
    { code: '+385', country: 'HR', flag: '🇭🇷', name: 'Croatia', nationality: 'Croatian' },
    { code: '+357', country: 'CY', flag: '🇨🇾', name: 'Cyprus', nationality: 'Cypriot' },
    { code: '+420', country: 'CZ', flag: '🇨🇿', name: 'Czech Republic', nationality: 'Czech' },
    { code: '+45', country: 'DK', flag: '🇩🇰', name: 'Denmark', nationality: 'Danish' },
    { code: '+372', country: 'EE', flag: '🇪🇪', name: 'Estonia', nationality: 'Estonian' },
    { code: '+358', country: 'FI', flag: '🇫🇮', name: 'Finland', nationality: 'Finnish' },
    { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France', nationality: 'French' },
    { code: '+995', country: 'GE', flag: '🇬🇪', name: 'Georgia', nationality: 'Georgian' },
    { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany', nationality: 'German' },
    { code: '+30', country: 'GR', flag: '🇬🇷', name: 'Greece', nationality: 'Greek' },
    { code: '+36', country: 'HU', flag: '🇭🇺', name: 'Hungary', nationality: 'Hungarian' },
    { code: '+354', country: 'IS', flag: '🇮🇸', name: 'Iceland', nationality: 'Icelander' },
    { code: '+353', country: 'IE', flag: '🇮🇪', name: 'Ireland', nationality: 'Irish' },
    { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy', nationality: 'Italian' },
    { code: '+371', country: 'LV', flag: '🇱🇻', name: 'Latvia', nationality: 'Latvian' },
    { code: '+423', country: 'LI', flag: '🇱🇮', name: 'Liechtenstein', nationality: 'Liechtensteiner' },
    { code: '+370', country: 'LT', flag: '🇱🇹', name: 'Lithuania', nationality: 'Lithuanian' },
    { code: '+352', country: 'LU', flag: '🇱🇺', name: 'Luxembourg', nationality: 'Luxembourgish' },
    { code: '+356', country: 'MT', flag: '🇲🇹', name: 'Malta', nationality: 'Maltese' },
    { code: '+373', country: 'MD', flag: '🇲🇩', name: 'Moldova', nationality: 'Moldovan' },
    { code: '+377', country: 'MC', flag: '🇲🇨', name: 'Monaco', nationality: 'Monegasque' },
    { code: '+382', country: 'ME', flag: '🇲🇪', name: 'Montenegro', nationality: 'Montenegrin' },
    { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Netherlands', nationality: 'Dutch' },
    { code: '+389', country: 'MK', flag: '🇲🇰', name: 'North Macedonia', nationality: 'Macedonian' },
    { code: '+47', country: 'NO', flag: '🇳🇴', name: 'Norway', nationality: 'Norwegian' },
    { code: '+48', country: 'PL', flag: '🇵🇱', name: 'Poland', nationality: 'Polish' },
    { code: '+351', country: 'PT', flag: '🇵🇹', name: 'Portugal', nationality: 'Portuguese' },
    { code: '+40', country: 'RO', flag: '🇷🇴', name: 'Romania', nationality: 'Romanian' },
    { code: '+7', country: 'RU', flag: '🇷🇺', name: 'Russia', nationality: 'Russian' },
    { code: '+378', country: 'SM', flag: '🇸🇲', name: 'San Marino', nationality: 'Sammarinese' },
    { code: '+379', country: 'VA', flag: '🇻🇦', name: 'Vatican', nationality: 'Vatican' },
    { code: '+381', country: 'RS', flag: '🇷🇸', name: 'Serbia', nationality: 'Serbian' },
    { code: '+421', country: 'SK', flag: '🇸🇰', name: 'Slovakia', nationality: 'Slovak' },
    { code: '+386', country: 'SI', flag: '🇸🇮', name: 'Slovenia', nationality: 'Slovenian' },
    { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain', nationality: 'Spanish' },
    { code: '+46', country: 'SE', flag: '🇸🇪', name: 'Sweden', nationality: 'Swedish' },
    { code: '+41', country: 'CH', flag: '🇨🇭', name: 'Switzerland', nationality: 'Swiss' },
    { code: '+380', country: 'UA', flag: '🇺🇦', name: 'Ukraine', nationality: 'Ukrainian' },
    { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom', nationality: 'British' },
    { code: '+383', country: 'XK', flag: '🇽🇰', name: 'Kosovo', nationality: 'Kosovar' },

    // Africa
    { code: '+213', country: 'DZ', flag: '🇩🇿', name: 'Algeria', nationality: 'Algerian' },
    { code: '+244', country: 'AO', flag: '🇦🇴', name: 'Angola', nationality: 'Angolan' },
    { code: '+229', country: 'BJ', flag: '🇧🇯', name: 'Benin', nationality: 'Beninese' },
    { code: '+267', country: 'BW', flag: '🇧🇼', name: 'Botswana', nationality: 'Motswana' },
    { code: '+226', country: 'BF', flag: '🇧🇫', name: 'Burkina Faso', nationality: 'Burkinabé' },
    { code: '+257', country: 'BI', flag: '🇧🇮', name: 'Burundi', nationality: 'Burundian' },
    { code: '+237', country: 'CM', flag: '🇨🇲', name: 'Cameroon', nationality: 'Cameroonian' },
    { code: '+238', country: 'CV', flag: '🇨🇻', name: 'Cape Verde', nationality: 'Cape Verdean' },
    { code: '+236', country: 'CF', flag: '🇨🇫', name: 'Central African Republic', nationality: 'Central African' },
    { code: '+235', country: 'TD', flag: '🇹🇩', name: 'Chad', nationality: 'Chadian' },
    { code: '+269', country: 'KM', flag: '🇰🇲', name: 'Comoros', nationality: 'Comoran' },
    { code: '+242', country: 'CG', flag: '🇨🇬', name: 'Congo', nationality: 'Congolese' },
    { code: '+243', country: 'CD', flag: '🇨🇩', name: 'Democratic Republic of the Congo', nationality: 'Congolese' },
    { code: '+225', country: 'CI', flag: '🇨🇮', name: `Côte d'Ivoire`, nationality: 'Ivorian' },
    { code: '+253', country: 'DJ', flag: '🇩🇯', name: 'Djibouti', nationality: 'Djiboutian' },
    { code: '+20', country: 'EG', flag: '🇪🇬', name: 'Egypt', nationality: 'Egyptian' },
    { code: '+291', country: 'ER', flag: '🇪🇷', name: 'Eritrea', nationality: 'Eritrean' },
    { code: '+251', country: 'ET', flag: '🇪🇹', name: 'Ethiopia', nationality: 'Ethiopian' },
    { code: '+240', country: 'GQ', flag: '🇬🇶', name: 'Equatorial Guinea', nationality: 'Equatorial Guinean' },
    { code: '+241', country: 'GA', flag: '🇬🇦', name: 'Gabon', nationality: 'Gabonese' },
    { code: '+220', country: 'GM', flag: '🇬🇲', name: 'Gambia', nationality: 'Gambian' },
    { code: '+233', country: 'GH', flag: '🇬🇭', name: 'Ghana', nationality: 'Ghanaian' },
    { code: '+224', country: 'GN', flag: '🇬🇳', name: 'Guinea', nationality: 'Guinean' },
    { code: '+245', country: 'GW', flag: '🇬🇼', name: 'Guinea-Bissau', nationality: 'Guinea-Bissauan' },
    { code: '+254', country: 'KE', flag: '🇰🇪', name: 'Kenya', nationality: 'Kenyan' },
    { code: '+266', country: 'LS', flag: '🇱🇸', name: 'Lesotho', nationality: 'Basotho' },
    { code: '+231', country: 'LR', flag: '🇱🇷', name: 'Liberia', nationality: 'Liberian' },
    { code: '+218', country: 'LY', flag: '🇱🇾', name: 'Libya', nationality: 'Libyan' },
    { code: '+261', country: 'MG', flag: '🇲🇬', name: 'Madagascar', nationality: 'Malagasy' },
    { code: '+265', country: 'MW', flag: '🇲🇼', name: 'Malawi', nationality: 'Malawian' },
    { code: '+223', country: 'ML', flag: '🇲🇱', name: 'Mali', nationality: 'Malian' },
    { code: '+222', country: 'MR', flag: '🇲🇷', name: 'Mauritania', nationality: 'Mauritanian' },
    { code: '+230', country: 'MU', flag: '🇲🇺', name: 'Mauritius', nationality: 'Mauritian' },
    { code: '+212', country: 'MA', flag: '🇲🇦', name: 'Morocco', nationality: 'Moroccan' },
    { code: '+258', country: 'MZ', flag: '🇲🇿', name: 'Mozambique', nationality: 'Mozambican' },
    { code: '+264', country: 'NA', flag: '🇳🇦', name: 'Namibia', nationality: 'Namibian' },
    { code: '+227', country: 'NE', flag: '🇳🇪', name: 'Niger', nationality: 'Nigerien' },
    { code: '+234', country: 'NG', flag: '🇳🇬', name: 'Nigeria', nationality: 'Nigerian' },
    { code: '+250', country: 'RW', flag: '🇷🇼', name: 'Rwanda', nationality: 'Rwandan' },
    { code: '+239', country: 'ST', flag: '🇸🇹', name: 'Sao Tome and Principe', nationality: 'São Toméan' },
    { code: '+221', country: 'SN', flag: '🇸🇳', name: 'Senegal', nationality: 'Senegalese' },
    { code: '+248', country: 'SC', flag: '🇸🇨', name: 'Seychelles', nationality: 'Seychellois' },
    { code: '+232', country: 'SL', flag: '🇸🇱', name: 'Sierra Leone', nationality: 'Sierra Leonean' },
    { code: '+252', country: 'SO', flag: '🇸🇴', name: 'Somalia', nationality: 'Somali' },
    { code: '+27', country: 'ZA', flag: '🇿🇦', name: 'South Africa', nationality: 'South African' },
    { code: '+211', country: 'SS', flag: '🇸🇸', name: 'South Sudan', nationality: 'South Sudanese' },
    { code: '+249', country: 'SD', flag: '🇸🇩', name: 'Sudan', nationality: 'Sudanese' },
    { code: '+255', country: 'TZ', flag: '🇹🇿', name: 'Tanzania', nationality: 'Tanzanian' },
    { code: '+228', country: 'TG', flag: '🇹🇬', name: 'Togo', nationality: 'Togolese' },
    { code: '+216', country: 'TN', flag: '🇹🇳', name: 'Tunisia', nationality: 'Tunisian' },
    { code: '+256', country: 'UG', flag: '🇺🇬', name: 'Uganda', nationality: 'Ugandan' },
    { code: '+260', country: 'ZM', flag: '🇿🇲', name: 'Zambia', nationality: 'Zambian' },
    { code: '+263', country: 'ZW', flag: '🇿🇼', name: 'Zimbabwe', nationality: 'Zimbabwean' },

    // North & South America
    { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States', nationality: 'American' },
    { code: '+1', country: 'CA', flag: '🇨🇦', name: 'Canada', nationality: 'Canadian' },
    { code: '+52', country: 'MX', flag: '🇲🇽', name: 'Mexico', nationality: 'Mexican' },
    { code: '+54', country: 'AR', flag: '🇦🇷', name: 'Argentina', nationality: 'Argentinian' },
    { code: '+591', country: 'BO', flag: '🇧🇴', name: 'Bolivia', nationality: 'Bolivian' },
    { code: '+55', country: 'BR', flag: '🇧🇷', name: 'Brazil', nationality: 'Brazilian' },
    { code: '+56', country: 'CL', flag: '🇨🇱', name: 'Chile', nationality: 'Chilean' },
    { code: '+57', country: 'CO', flag: '🇨🇴', name: 'Colombia', nationality: 'Colombian' },
    { code: '+506', country: 'CR', flag: '🇨🇷', name: 'Costa Rica', nationality: 'Costa Rican' },
    { code: '+53', country: 'CU', flag: '🇨🇺', name: 'Cuba', nationality: 'Cuban' },
    { code: '+1', country: 'DO', flag: '🇩🇴', name: 'Dominican Republic', nationality: 'Dominican' },
    { code: '+593', country: 'EC', flag: '🇪🇨', name: 'Ecuador', nationality: 'Ecuadorian' },
    { code: '+503', country: 'SV', flag: '🇸🇻', name: 'El Salvador', nationality: 'Salvadoran' },
    { code: '+592', country: 'GY', flag: '🇬🇾', name: 'Guyana', nationality: 'Guyanese' },
    { code: '+502', country: 'GT', flag: '🇬🇹', name: 'Guatemala', nationality: 'Guatemalan' },
    { code: '+509', country: 'HT', flag: '🇭🇹', name: 'Haiti', nationality: 'Haitian' },
    { code: '+504', country: 'HN', flag: '🇭🇳', name: 'Honduras', nationality: 'Honduran' },
    { code: '+1', country: 'JM', flag: '🇯🇲', name: 'Jamaica', nationality: 'Jamaican' },
    { code: '+505', country: 'NI', flag: '🇳🇮', name: 'Nicaragua', nationality: 'Nicaraguan' },
    { code: '+507', country: 'PA', flag: '🇵🇦', name: 'Panama', nationality: 'Panamanian' },
    { code: '+595', country: 'PY', flag: '🇵🇾', name: 'Paraguay', nationality: 'Paraguayan' },
    { code: '+51', country: 'PE', flag: '🇵🇪', name: 'Peru', nationality: 'Peruvian' },
    { code: '+1', country: 'PR', flag: '🇵🇷', name: 'Puerto Rico', nationality: 'Puerto Rican' },
    { code: '+598', country: 'UY', flag: '🇺🇾', name: 'Uruguay', nationality: 'Uruguayan' },
    { code: '+597', country: 'SR', flag: '🇸🇷', name: 'Suriname', nationality: 'Surinamese' },
    { code: '+58', country: 'VE', flag: '🇻🇪', name: 'Venezuela', nationality: 'Venezuelan' },

    // Oceania
    { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia', nationality: 'Australian' },
    { code: '+679', country: 'FJ', flag: '🇫🇯', name: 'Fiji', nationality: 'Fijian' },
    { code: '+686', country: 'KI', flag: '🇰🇮', name: 'Kiribati', nationality: 'I-Kiribati' },
    { code: '+692', country: 'MH', flag: '🇲🇭', name: 'Marshall Islands', nationality: 'Marshallese' },
    { code: '+691', country: 'FM', flag: '🇫🇲', name: 'Micronesia', nationality: 'Micronesian' },
    { code: '+674', country: 'NR', flag: '🇳🇷', name: 'Nauru', nationality: 'Nauruan' },
    { code: '+64', country: 'NZ', flag: '🇳🇿', name: 'New Zealand', nationality: 'New Zealander' },
    { code: '+680', country: 'PW', flag: '🇵🇼', name: 'Palau', nationality: 'Palauan' },
    { code: '+675', country: 'PG', flag: '🇵🇬', name: 'Papua New Guinea', nationality: 'Papua New Guinean' },
    { code: '+685', country: 'WS', flag: '🇼🇸', name: 'Samoa', nationality: 'Samoan' },
    { code: '+677', country: 'SB', flag: '🇸🇧', name: 'Solomon Islands', nationality: 'Solomon Islander' },
    { code: '+676', country: 'TO', flag: '🇹🇴', name: 'Tonga', nationality: 'Tongan' },
    { code: '+688', country: 'TV', flag: '🇹🇻', name: 'Tuvalu', nationality: 'Tuvaluan' },
    { code: '+678', country: 'VU', flag: '🇻🇺', name: 'Vanuatu', nationality: 'Ni-Vanuatu' },
] as const;

/**
 * Find a country by its phone code (with or without + prefix)
 */
export function findCountryByCode(code: string): Country | undefined {
    // Normalize code: ensure it starts with +
    const normalizedCode = code.startsWith('+') ? code : `+${code}`;
    return COUNTRIES.find(c => c.code === normalizedCode);
}

/**
 * Find a country by its ISO 3166-1 alpha-2 country code
 */
export function findCountryByCountryCode(countryCode: string): Country | undefined {
    return COUNTRIES.find(c => c.country.toLowerCase() === countryCode.toLowerCase());
}

/**
 * Find a country by its name (case-insensitive partial match)
 */
export function findCountryByName(name: string): Country | undefined {
    const lowerName = name.toLowerCase();
    return COUNTRIES.find(c => c.name.toLowerCase().includes(lowerName));
}

/**
 * Get all countries sorted alphabetically by name
 */
export function getCountriesSortedByName(): readonly Country[] {
    return [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get all countries sorted by phone code
 */
export function getCountriesSortedByCode(): readonly Country[] {
    return [...COUNTRIES].sort((a, b) => {
        // Remove + and compare as numbers
        const codeA = parseInt(a.code.replace('+', ''), 10);
        const codeB = parseInt(b.code.replace('+', ''), 10);
        return codeA - codeB;
    });
}

/**
 * Get default country (UAE)
 */
export function getDefaultCountry(): Country {
    return COUNTRIES.find(c => c.country === 'AE') || COUNTRIES[0]!;
}

/**
 * Get all countries as nationality items (for nationality dropdown)
 * Returns countries with code, country, flag, name, and nationality fields
 */
export function getNationalities(): readonly Country[] {
    return COUNTRIES;
}

/**
 * Extract numeric code from phone code string
 * Example: "+971" -> "971"
 */
export function extractNumericCode(code: string): string {
    return code.replace('+', '');
}

/**
 * Format phone code with + prefix
 * Example: "971" -> "+971"
 */
export function formatPhoneCode(code: string): string {
    return code.startsWith('+') ? code : `+${code}`;
}

/**
 * Parse phone number to extract country code and local number
 * Uses centralized COUNTRIES list as single source of truth
 * 
 * @param raw - Raw phone number string (may or may not have + prefix)
 * @returns Object with code (e.g., "+971") and local (local number without country code)
 */
export function parsePhoneNumber(raw: string): { code: string; local: string } {
    if (!raw) {
        const defaultCountry = getDefaultCountry();
        return { code: defaultCountry.code, local: '' };
    }
    
    const cleaned = raw.replace(/\s+/g, '').trim();
    
    // If already has +, try to match against known country codes
    if (cleaned.startsWith('+')) {
        // Try to match longest codes first (e.g., +971 before +97)
        for (let len = 4; len >= 1; len--) {
            const codeCandidate = cleaned.substring(0, len + 1); // +1 for the +
            if (codeCandidate.startsWith('+')) {
                const country = COUNTRIES.find(c => c.code === codeCandidate);
                if (country) {
                    return {
                        code: country.code,
                        local: cleaned.replace(codeCandidate, '').trim()
                    };
                }
            }
        }
        // Fallback: extract first 1-3 digits after +
        const codeMatch = cleaned.match(/^\+(\d{1,3})/)?.[1];
        const defaultCountry = getDefaultCountry();
        return {
            code: codeMatch ? `+${codeMatch}` : defaultCountry.code,
            local: cleaned.replace(/^\+?\d{1,3}/, '').trim()
        };
    }
    
    // No + prefix: try to match against known country codes (numeric codes only)
    // Try longest codes first for better matching (e.g., 971 before 97)
    const sortedCodes = [...COUNTRIES]
        .map(c => ({ code: extractNumericCode(c.code), country: c }))
        .sort((a, b) => b.code.length - a.code.length); // Sort by length descending
    
    for (const { code, country } of sortedCodes) {
        if (cleaned.startsWith(code)) {
            return {
                code: country.code,
                local: cleaned.slice(code.length).trim()
            };
        }
    }
    
    // Default to UAE if no match
    const defaultCountry = getDefaultCountry();
    return { code: defaultCountry.code, local: cleaned };
}

